<template>
  <modal
    :name="name"
    :width="860"
    :height="'auto'"
    :scrollable="true"
    :adaptive="true"
    :click-to-close="false"
    :reset="true"
    class="sales-leave-change-modal modal-not-overflow"
    draggable=".app-modal-header__copy"
  >
    <section v-if="leave" class="sales-leave-change-modal__content" aria-labelledby="sales-leave-change-title">
      <app-modal-header design="new" title="Request date change" title-id="sales-leave-change-title" @close="close" />
      <form class="sales-leave-change-modal__body" @submit.prevent="submit">
        <div class="sales-leave-change-modal__columns">
          <div class="sales-leave-change-modal__fields">
            <h3>New dates</h3>
            <p>Choose the dates you want to request.</p>
            <div class="sales-leave-change__dates">
              <app-form-group design="new" label="New start" required>
                <app-date-picker
                  v-model="form.from_date"
                  design="new"
                  placeholder="New start"
                  :disabled="!limits.canMoveStart"
                  :disabled-date="isFromDisabled"
                  :error="$v.form.from_date.$error"
                />
                <template #error>
                  <div v-if="$v.form.from_date.$dirty && !$v.form.from_date.required">Required field</div>
                </template>
              </app-form-group>
              <app-form-group design="new" label="New end" required>
                <app-date-picker
                  v-model="form.to_date"
                  design="new"
                  placeholder="New end"
                  :disabled-date="isToDisabled"
                  :error="$v.form.to_date.$error"
                />
                <template #error>
                  <div v-if="$v.form.to_date.$dirty && !$v.form.to_date.required">Required field</div>
                </template>
              </app-form-group>
            </div>
            <app-form-group design="new" label="Reason" required>
              <app-textarea
                v-model="form.reason"
                design="new"
                placeholder="Explain why you need to change the dates"
                :error="$v.form.reason.$error"
              />
              <template #error>
                <div v-if="$v.form.reason.$dirty && !$v.form.reason.reasonPresent">Required field</div>
              </template>
            </app-form-group>
            <div v-if="visibleError" class="sales-leave-error" role="alert">{{ visibleError }}</div>
            <div v-if="submitError" class="sales-leave-error" role="alert">{{ submitError }}</div>
          </div>
          <aside class="sales-leave-change-modal__info" aria-label="Leave change details">
            <div class="sales-leave-change__current">
              <span class="sales-leave-change__current-label">Current · {{ leave.type.name }}</span>
              <strong>{{ formatDate(leave.from_date) }} – {{ formatDate(leave.to_date) }}</strong>
              <span>{{ leave.days }} calendar days</span>
            </div>
            <div v-if="hasDateChanges && newDays" class="sales-leave-change__requested">
              <span>Requested</span>
              <strong>{{ formatDate(form.from_date) }} – {{ formatDate(form.to_date) }}</strong>
              <span>{{ newDays }} calendar days</span>
            </div>
            <p v-else class="sales-leave-change__notice">Choose new dates to compare them here.</p>
            <p class="sales-leave-change__approval">Current dates remain until approval.</p>
            <sales-leave-annual :annual="annual" compact />
          </aside>
        </div>
        <footer class="sales-leave-change-modal__footer">
          <app-button design="new" theme="dark" type="button" :disabled="submitting" @click.native="close">Cancel</app-button>
          <app-button ref="submit" design="new" theme="info" :disabled="submitting" type="submit">Send request</app-button>
        </footer>
      </form>
    </section>
  </modal>
</template>

<script>
import debounce from 'lodash/debounce'
import { required } from 'vuelidate/lib/validators'
import SalesLeaveAnnual from './SalesLeaveAnnual'
import {
  PAID_CODE,
  businessToday,
  changeDateLimits,
  errorMessage,
  inclusiveDays,
  validateChange,
} from './salesLeave'
import { formatDate } from './format'

export default {
  name: 'SalesLeaveChangeModal',
  components: { SalesLeaveAnnual },
  props: {
    name: {
      type: String,
      default: 'sales-leave-change'
    },
    // (leave, payload) => Promise: creates the change request
    submitRequest: {
      type: Function,
      required: true
    },
    // (leave, payload) => Promise: annual Paid preview
    previewRequest: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      leave: null,
      form: { from_date: null, to_date: null, reason: '' },
      annual: null,
      submitting: false,
      submitError: '',
      submitAttempted: false
    }
  },
  validations: {
    form: {
      from_date: { required },
      to_date: { required },
      reason: {
        reasonPresent(value) {
          return Boolean((value || '').trim())
        }
      }
    }
  },
  computed: {
    limits() {
      return this.leave ? changeDateLimits(this.leave) : { canMoveStart: false, minEnd: null }
    },
    newDays() {
      const { from_date: from, to_date: to } = this.form
      return from && to && from < to ? inclusiveDays(from, to) : 0
    },
    hasDateChanges() {
      return Boolean(this.leave && (
        this.form.from_date !== this.leave.from_date
        || this.form.to_date !== this.leave.to_date
      ))
    },
    localError() {
      if (!this.leave || !this.form.from_date || !this.form.to_date) return ''
      return validateChange(this.leave, this.form)
    },
    visibleError() {
      if (!this.submitAttempted && !this.hasDateChanges) return ''
      if (!this.submitAttempted && this.localError === 'Reason must not be blank.') return ''
      return this.localError
    }
  },
  watch: {
    'form.from_date'() { this.refreshPreview() },
    'form.to_date'() { this.refreshPreview() }
  },
  methods: {
    formatDate,
    open(leave) {
      this.leave = leave
      this.form = { from_date: leave.from_date, to_date: leave.to_date, reason: '' }
      this.annual = null
      this.submitError = ''
      this.submitAttempted = false
      this.$v.form.$reset()
      this.$modal.show(this.name)
    },
    close() {
      this.$modal.hide(this.name)
    },
    dateValue(date) {
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    },
    isFromDisabled(date) {
      return this.dateValue(date) <= businessToday()
    },
    isToDisabled(date) {
      const value = this.dateValue(date)
      return Boolean(this.form.from_date && value <= this.form.from_date)
    },
    refreshPreview: debounce(function () {
      this.annual = null
      if (!this.leave || this.leave.type.code !== PAID_CODE || !this.previewRequest) return
      const { from_date: from, to_date: to } = this.form
      if (!from || !to || validateChange(this.leave, { ...this.form, reason: 'x' })) return
      this.previewRequest(this.leave, { from_date: from, to_date: to })
        .then(res => { this.annual = res.data.annual })
        .catch(() => {})
    }, 300),
    submit() {
      this.submitAttempted = true
      this.$v.form.$touch()
      if (this.$v.form.$invalid || this.localError) return
      this.submitting = true
      this.submitError = ''
      this.submitRequest(this.leave, {
        from_date: this.form.from_date,
        to_date: this.form.to_date,
        reason: this.form.reason.trim()
      })
        .then(() => {
          this.$notify({ type: 'success', title: 'Date change sent for approval' })
          this.close()
          this.$emit('created')
        })
        .catch(error => { this.submitError = errorMessage(error) })
        .finally(() => { this.submitting = false })
    }
  }
}
</script>

<style lang="scss">
.sales-leave-change-modal > .vm--modal {
  border-radius: $ui-card-radius !important;
}
</style>

<style lang="scss" scoped>
.sales-leave-change-modal__content {
  border-radius: $ui-card-radius;
  background: $ui-surface;
  color: $ui-text;
  font-family: $ui-font-body;

  :deep(.app-modal-header--new) {
    border-radius: $ui-card-radius $ui-card-radius 0 0;
  }
}

.sales-leave-change-modal__body {
  padding: $ui-space-24;
}

.sales-leave-change-modal__columns {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: $ui-space-24;
}

.sales-leave-change-modal__fields,
.sales-leave-change-modal__info {
  min-width: 0;
}

.sales-leave-change-modal__fields,
.sales-leave-change-modal__info {
  h3 {
    margin: 0 0 $ui-space-8;
    color: $ui-ink;
    font-family: $ui-font-heading;
    font-size: $ui-font-size-body;
    font-weight: 700;
  }

  p {
    margin: 0 0 $ui-space-20;
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    line-height: 1.5;
  }
}

.sales-leave-change-modal__info {
  padding: $ui-space-16;
  border: 1px solid $ui-line;
  border-radius: $ui-panel-radius;
  background: $ui-surface-soft;

  p { margin-bottom: 0; }

  :deep(.sales-leave-annual) {
    margin: $ui-space-16 0 0;
  }
}

.sales-leave-change-modal__footer {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: $ui-space-12;
  margin-top: $ui-space-24;
}

.sales-leave-change__current {
  display: grid;
  gap: $ui-space-4;
  padding-bottom: $ui-space-12;

  strong {
    color: $ui-ink;
    font-size: $ui-font-size-body;
  }

  span { color: $ui-muted; }
}

.sales-leave-change__current-label {
  font-size: $ui-font-size-metadata;
  font-weight: 600;
}

.sales-leave-change__dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $ui-space-16;

  .form-group--design-new { min-width: 0; }
}

.sales-leave-change__notice {
  margin: 0;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
  line-height: 1.5;
}

.sales-leave-change__requested {
  display: grid;
  gap: $ui-space-4;
  padding-top: $ui-space-12;
  border-top: 1px solid $ui-line;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;

  strong {
    color: $ui-ink;
    font-size: $ui-font-size-body;
  }
}

.sales-leave-change-modal__info .sales-leave-change__approval {
  margin: $ui-space-16 0 0;
  color: $ui-muted;
  font-size: $ui-font-size-metadata;
}

.sales-leave-error {
  margin: $ui-space-8 0;
  color: $ui-red;
  font-size: $ui-font-size-body;
}

@media (max-width: 720px) {
  .sales-leave-change-modal__columns { grid-template-columns: 1fr; }
}

@media (max-width: 600px) {
  .sales-leave-change__dates { grid-template-columns: 1fr; }
}
</style>
